import type { DataProvider, RaRecord } from 'react-admin';
import { API_URL, TOKEN_STORAGE_KEY } from '../config';

/**
 * Кастомный DataProvider под наш REST API.
 *
 * Наш API использует cursor-pagination ({items, nextCursor, hasMore}),
 * react-admin ждёт {data, total} с offset-пагинацией. Адаптируем.
 *
 * Маппинг resource → endpoint:
 *   users        → /staff/users
 *   photos       → /staff/photos
 *   reports      → /staff/reports
 *   invitations  → /staff/invitations
 *   auditLogs    → /staff/audit-log
 */
const RESOURCE_PATHS: Record<string, string> = {
  users: '/staff/users',
  photos: '/staff/photos',
  reports: '/staff/reports',
  invitations: '/staff/invitations',
  auditLogs: '/staff/audit-log',
};

/** Ресурсы с detail-endpoint GET /staff/<resource>/:id (агрегаты для Show-страниц). */
const DETAIL_RESOURCES = new Set(['users', 'reports', 'invitations']);

function authHeaders(): Record<string, string> {
  const token = localStorage.getItem(TOKEN_STORAGE_KEY);
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function api<T>(
  method: string,
  path: string,
  body?: Record<string, unknown>,
  query?: Record<string, unknown>,
): Promise<T> {
  const url = new URL(`${API_URL}${path}`);
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined && v !== null && v !== '') {
        url.searchParams.set(k, String(v));
      }
    }
  }
  const res = await fetch(url.toString(), {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders(),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    const err: Error & { status?: number; body?: string } = new Error(
      `${res.status} ${res.statusText}: ${text}`,
    );
    err.status = res.status;
    err.body = text;
    throw err;
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

function resourcePath(resource: string): string {
  const p = RESOURCE_PATHS[resource];
  if (!p) throw new Error(`Unknown resource: ${resource}`);
  return p;
}

interface PaginatedResponse<T> {
  items: T[];
  nextCursor: string | null;
  hasMore: boolean;
}

export const dataProvider: DataProvider = {
  async getList<RecordType extends RaRecord = RaRecord>(
    resource: string,
    params: {
      pagination?: { perPage?: number };
      filter?: Record<string, unknown>;
    },
  ) {
    const path = resourcePath(resource);
    const limit = params.pagination?.perPage ?? 50;
    const filter = params.filter ?? {};
    const cursor = (filter as { _cursor?: string })._cursor;
    const cleanedFilter: Record<string, unknown> = { ...filter };
    delete cleanedFilter._cursor;

    const res = await api<PaginatedResponse<RecordType>>('GET', path, undefined, {
      limit,
      cursor,
      ...cleanedFilter,
    });

    return {
      data: res.items,
      pageInfo: {
        hasNextPage: res.hasMore,
        hasPreviousPage: false,
      },
      total: res.hasMore ? res.items.length + limit : res.items.length,
    };
  },

  async getOne<RecordType extends RaRecord = RaRecord>(
    resource: string,
    params: { id: string | number },
  ) {
    const path = resourcePath(resource);
    // Для ресурсов с detail-endpoint — полноценный агрегат одним запросом
    if (DETAIL_RESOURCES.has(resource)) {
      const data = await api<RecordType>('GET', `${path}/${String(params.id)}`);
      return { data };
    }
    // Fallback для остальных: поиск по первой странице списка
    const res = await api<PaginatedResponse<RecordType>>('GET', path, undefined, { limit: 100 });
    const found = res.items.find((i) => String(i.id) === String(params.id));
    if (!found) throw new Error(`Not found in ${resource}: ${params.id}`);
    return { data: found };
  },

  async getMany<RecordType extends RaRecord = RaRecord>(
    resource: string,
    params: { ids: ReadonlyArray<string | number> },
  ) {
    const path = resourcePath(resource);
    const res = await api<PaginatedResponse<RecordType>>('GET', path, undefined, { limit: 100 });
    const ids = new Set(params.ids.map(String));
    return { data: res.items.filter((i) => ids.has(String(i.id))) };
  },

  async getManyReference() {
    throw new Error('getManyReference not implemented');
  },

  async update() {
    throw new Error('Use custom actions for updates (Buttons)');
  },

  async updateMany() {
    throw new Error('Bulk update not implemented');
  },

  async create() {
    throw new Error('Create not implemented for staff resources');
  },

  async delete() {
    throw new Error('Delete not implemented');
  },

  async deleteMany() {
    throw new Error('Bulk delete not implemented');
  },
};

/** Вспомогательная функция для кастомных action-кнопок. */
export async function staffAction<T = unknown>(
  method: string,
  path: string,
  body?: Record<string, unknown>,
): Promise<T> {
  return api<T>(method, path, body);
}
